import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import importListHelper from '../../../Helpers/importListHelper';
import TextField from './TextField';
import TextBox from './TextBox';
import FaIcon from '../../FaIcon';
import FigmaButton from '../../FigmaButton';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';

/**
 * A text field for a comma-separated list of values (`InputTypesEnum.TextList`). Next to the field is an
 * "Import list" button that opens a box to paste a column of cells copied from Excel into; importing it
 * writes the values back as `a,b,c`. Pasting several cells straight into the field does the same.
 */
export default function ImportListTextField({
  className = 'w-100 form-control form-control-sm',
  fieldName,
  labelText = '',
  onChange,
  placeholder = '',
  value = null,
}: {
  className?: string;
  fieldName: string;
  labelText?: string;
  onChange: (fieldName: string, value: string) => void;
  placeholder?: string;
  value?: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [listText, setListText] = useState('');
  const [position, setPosition] = useState<{ right: number; top?: number; bottom?: number }>({ right: 0 });
  const fieldRef = useRef<HTMLDivElement>(null);

  const values = importListHelper.ParseImportList(listText);
  const importedValue = importListHelper.ToImportListValue(values);
  // Upper bound on the comma-joined list one field may hold. Search filters travel in the request URL,
  // which `authService.CallBackendFunction` caps at 2000 characters for the whole search — this keeps one
  // list well inside that, leaving room for the other filters.
  const isTooLong = importedValue.length > 1200;

  const open = () => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (rect) {
      // Right-aligned under the field; opens upwards when there is not room for the box (~340px) below it.
      const right = Math.max(window.innerWidth - rect.right, 8);
      setPosition(
        window.innerHeight - rect.bottom >= 340
          ? { right, top: rect.bottom + 6 }
          : { right, bottom: window.innerHeight - rect.top + 6 }
      );
    }
    setListText(importListHelper.ToImportListText(value));
    setIsOpen(true);
  };

  const importList = () => {
    onChange(fieldName, importedValue);
    setIsOpen(false);
  };

  // A single-line input silently drops the line breaks of a pasted column ("123\n124" arrives as "123124"),
  // so a multi-cell paste is turned into the comma list here instead of reaching the input.
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (!importListHelper.IsMultiValuePaste(pasted)) {
      return;
    }
    e.preventDefault();
    const current = value ?? '';
    const start = e.currentTarget.selectionStart ?? current.length;
    const end = e.currentTarget.selectionEnd ?? current.length;
    const merged = `${current.slice(0, start)}\n${pasted}\n${current.slice(end)}`;
    onChange(fieldName, importListHelper.ToImportListValue(importListHelper.ParseImportList(merged)));
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') {
      return;
    }
    // Close only this box: the filter panel closes itself on Escape through a document listener.
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setIsOpen(false);
  };

  const renderCount = () => {
    if (isTooLong) {
      return (
        <div className='import-list-msg error'>
          The list is too long to search at once. Please split it into smaller lists.
        </div>
      );
    }
    return (
      <div className='import-list-msg'>
        {values.length === 1 ? '1 value' : `${values.length} values`}
        {values.length === 0 && listText.trim() === '' ? ' — importing clears the field' : ''}
      </div>
    );
  };

  const renderPopover = () =>
    createPortal(
      // The portal is outside the field's DOM but inside its React tree, so clicks would still bubble up to
      // whatever hosts the field (a grid row, an edit wizard) — stop them here.
      <div
        className='bravo-ds import-list-layer'
        onKeyDown={handlePopoverKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className='import-list-backdrop'
          onClick={() => setIsOpen(false)}
        />
        <div
          className='pop import-list-pop'
          role='dialog'
          aria-label={`Import list${labelText ? ` — ${labelText}` : ''}`}
          style={position}
        >
          <div className='pop-head'>
            <span className='pic'>
              <i className='fa-solid fa-list-ul' />
            </span>
            <div>
              <div className='pt'>Import list</div>
              {labelText && <div className='ps'>{labelText}</div>}
            </div>
          </div>
          <div className='pop-body'>
            <TextBox
              className='import-list-textarea'
              numberOfRows={10}
              value={listText}
              placeholder='Paste the cells copied from Excel — one value per line'
              onChange={setListText}
              fieldName={`${fieldName}ImportList`}
              autoFocus
            />
            {renderCount()}
          </div>
          <div className='pop-foot'>
            <FigmaButton
              label='Close'
              type={ButtonTypeEnum.NormalNormal}
              color={ButtonColorsEnum.Common}
              onClick={() => setIsOpen(false)}
            />
            <FigmaButton
              label='Import list'
              type={ButtonTypeEnum.NormalNormal}
              color={ButtonColorsEnum.Primary}
              disabled={isTooLong}
              onClick={importList}
            />
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      ref={fieldRef}
      className='d-flex w-100'
    >
      <TextField
        className={className}
        fieldName={fieldName}
        onChange={onChange}
        onPaste={handlePaste}
        placeholder={placeholder}
        value={value}
      />
      <FaIcon
        className='btn btn-info btn-sm form-control-orange'
        iconName='fa-solid fa-list-ul'
        tooltipText='Import list — paste cells copied from Excel'
        onClick={open}
      />
      {isOpen && renderPopover()}
    </div>
  );
}
